import type { IPresetResponse } from '../../apis/client-side/preset/type';
import type { ASPInfo, CoreConfig } from '../../config/type';
import { queryClient } from '../../queries';
import { memberQueryKeys } from '../../queries/member/memberQueryKeys';
import { useASPStore } from '../../stores/useASPStore';
import { ContextualError } from '../error/context-error';
import parseJsonSafely from '../parseJsonSafely';

export type ASPOutgoingMessage =
  | { type: 'init' } // 페이지 로드완료
  | { type: 'exit' } // v1/asp/auth 에서 오류메시지로 “aspExit” 가 온 경우
  | { type: 'scrollTop' } // 페이지 이동 시 상위로 스크롤 필요한 경우
  | { type: 'fullLayout'; onoff: boolean } // 방송 시청 레이아웃 전환 시
  | { type: 'login' } // 로그인 요청 (사용X)
  | { type: 'authAdult' } // 본인인증 요청 (사용X)
  | { type: 'usePoint'; info: { coin: number; point: number } } // 하트선물 사용 포인트 정보 전달
  | { type: 'showErrorHtml'; html: string } // 오류 페이지 띄우는 경우
  | { type: 'openPopup'; url: string; name: string; options?: unknown } // 아이템 구매 결제 창 팝업 띄울 때
  | { type: 'resize'; height: number } // iframe 내부 높이 변경 발생 시
  | { type: 'scroll' } // 스크롤 이벤트 발생 시
  | { type: 'charge' } // ASP사 포인트 충전 페이지 호출 필요 시
  | { type: 'fullScreen'; onoff: boolean }; // 방송 시청 풀스크린 on/off 시 (사용X)
// | { type: 'getFullScreenState' } // 풀스크린 여부 확인 (사용X)
// | { type: 'reload' } // 외부에서 location.reload 호출 필요한 경우 (사용X)

export type ASPIncomingMessage =
  | { type: 'init' } // 외부창에서 init 수신 완료 시
  | { type: 'scroll'; scrollTop: number; height: number; iframeHeight?: number } // iframeHeight 추가
  | { type: 'config'; config: unknown } // init 시 외부 config 값 전달
  | { type: 'reload' } // iframe 내부 reload 요청
  | { type: 'location'; url: string } // iframe 내부 주소 이동 요청
  | { type: 'resize'; height: number } // 외부창 사이즈 변경 시 host does send, but vue does not use it.
  | { type: 'forceRefreshCoin' } // 유저 정보 최신화 요청
  | { type: 'refreshCoin' }; // 유저 정보 최신화 요청
// | { type: 'getFullScreenState'; state: boolean; isSupport: boolean }; // 외부창 풀스크린 여부 확인 (사용X)
// | { type: 'customAlert'; message: string } // iframe 외부에서 custom alert 사용 (사용X)

type EmittableEvents = 'navigate' | 'no-access';

const aspIncomingTypes = new Set<ASPIncomingMessage['type']>([
  'init',
  'config',
  'scroll',
  'location',
  'reload',
  'resize',
  'forceRefreshCoin',
  'refreshCoin',
] as ASPIncomingMessage['type'][]);

const isASPIncomingMessage = (message: unknown): message is ASPIncomingMessage => {
  if (!message) return false;
  if (typeof message !== 'object') return false;
  if (!('type' in message)) return false;
  if (typeof message.type !== 'string') return false;
  if (!aspIncomingTypes.has(message.type as ASPIncomingMessage['type'])) return false;
  return true;
};
// Outside class (or in a utils file)
export class ASPUtils {
  public siteInfo: IPresetResponse | null = null;
  public serviceType: 'popup' | 'iframe' = 'popup'; // default to 'popup' 새창타입, iframe 타입
  public aspInfo: ASPInfo;

  private prevHeight: number = 0;
  private coreConfig: CoreConfig;
  // 초기값 '*' (브로드캐스트). 첫 검증된 incoming message 의 origin 으로 좁힌다.
  // urlPc/urlMobile 은 파트너가 등록한 마케팅 URL 로 실제 부모 origin 과 스킴/포트가
  // 다를 수 있어 부트스트랩 targetOrigin 으로 쓰면 안 된다. (legacy pandatv-web-pc 패턴)
  private targetOrigin: string = '*';

  /**
   * asp 내 routing 관리
   */
  private listeners = {} as Record<EmittableEvents, Array<(...args: unknown[]) => void>>;
  public isFullScreen: boolean = false;

  constructor(aspInfo: ASPInfo, serviceType: 'popup' | 'iframe', coreConfig: CoreConfig) {
    this.aspInfo = aspInfo;
    this.coreConfig = coreConfig;
    if (typeof window !== 'undefined') {
      this.serviceType = serviceType;
      window.addEventListener('message', this.handleMessage);
      this.initResizeObserver();
      this.init();
    }
  }

  private readonly initResizeObserver = () => {
    if (typeof window === 'undefined' || !window.ResizeObserver) return;
    const resizeObserver = new ResizeObserver(() => {
      const currentHeight = document.documentElement.offsetHeight;
      if (currentHeight !== this.prevHeight) {
        this.prevHeight = currentHeight;
        this.resize(currentHeight);
      }
    });
    resizeObserver.observe(document.body);
  };

  public destroy() {
    window.removeEventListener('message', this.handleMessage);
  }

  public on(ev: EmittableEvents, listener: (...args: any[]) => void) {
    this.listeners[ev] = this.listeners[ev] || [];
    this.listeners[ev].push(listener);
  }
  public off(ev: EmittableEvents, listener: (...args: any[]) => void) {
    if (!this.listeners[ev]) return;
    this.listeners[ev] = this.listeners[ev].filter((l) => l !== listener);
  }

  public emit(ev: EmittableEvents, ...args: any[]) {
    if (!this.listeners[ev]) return;
    this.listeners[ev].forEach((listener) => listener(...args));
  }

  private readonly handleMessage = (ev: MessageEvent<string>): void => {
    if (!this.aspInfo?.domain) return;
    let originHost: string;
    try {
      originHost = new URL(ev.origin).hostname;
    } catch {
      return;
    }
    const partnerDomain = this.aspInfo.domain;
    if (originHost !== partnerDomain && !originHost.endsWith(`.${partnerDomain}`)) {
      return;
    }

    // 검증된 origin 으로 targetOrigin 을 좁힌다. 이후 send() 는 파트너 origin 으로만 postMessage 한다.
    if (ev.origin !== this.targetOrigin) {
      this.targetOrigin = ev.origin;
    }

    const parsed: unknown = parseJsonSafely(ev.data);
    if (isASPIncomingMessage(parsed) === false) {
      return;
    }

    const incomingMsg = parsed;

    // if (incomingMsg.type !== 'scroll') console.log('%cASPUtils::Received message:', 'color: green;', incomingMsg);
    console.log('%cASPUtils::Received message:', 'color: green;', incomingMsg);

    switch (incomingMsg.type) {
      case 'init':
        this.resize(document.documentElement.offsetHeight);
        this.scroll();
        break;

      case 'scroll':
        if (incomingMsg.scrollTop !== undefined) {
          useASPStore.getState().update({
            scrollTop: incomingMsg.scrollTop,
            height: incomingMsg.height,
            iframeHeight: incomingMsg.iframeHeight || 0,
            viewportHeight: incomingMsg.height,
          });
          useASPStore.getState().update({
            bottom: incomingMsg.scrollTop + incomingMsg.height,
          });
        }
        break;
      case 'config':
        useASPStore.getState().update({
          config: incomingMsg.config,
        });

        break;
      case 'reload':
        // reload 직후 showErrorHtml 이 따라 오는 시퀀스가 있음 — listener 가 큐로 받음.
        location.reload();
        break;
      case 'location':
        if (incomingMsg.url) {
          location.href = incomingMsg.url;
        }
        break;
      case 'forceRefreshCoin':
      case 'refreshCoin':
        queryClient.refetchQueries({ queryKey: memberQueryKeys.fetchMemberLoginInfo().queryKey });
        break;
      case 'resize': {
        const { scrollTop, iframeHeight } = useASPStore.getState();
        const viewportHeight = incomingMsg.height;

        useASPStore.getState().update({
          height: viewportHeight,
          viewportHeight,
          iframeHeight,
          bottom: scrollTop + viewportHeight,
        });
        break;
      }
      default:
        break;
    }
  };

  private send(msg: ASPOutgoingMessage) {
    if (typeof window === 'undefined' || !window.parent || window.self === window.top) return;
    if (!this.targetOrigin) {
      throw new ContextualError('Target origin is not defined');
    }
    const stringifiedMsg = JSON.stringify(msg);
    // if (msg.type !== 'scroll') console.log('%cASPUtils::Sending message:', 'color: blue;', stringifiedMsg);
    console.log('%cASPUtils::Sending message:', 'color: blue;', stringifiedMsg);

    window.parent.postMessage(stringifiedMsg, this.targetOrigin);
  }
  public init() {
    this.send({ type: 'init' });
  }
  public exit() {
    this.send({ type: 'exit' });
  }

  public scrollTop() {
    this.send({ type: 'scrollTop' });
  }
  public fullLayout(onoff: boolean) {
    this.send({ type: 'fullLayout', onoff });
  }
  public fullScreen(onoff: boolean) {
    this.send({ type: 'fullScreen', onoff: onoff });
    useASPStore.getState().update({ isFullScreen: onoff });
  }
  public usePoint(info: { coin: number; point: number }) {
    this.send({ type: 'usePoint', info });
  }
  public showErrorHtml(html: string) {
    this.send({ type: 'showErrorHtml', html });
  }
  public openPopup(url: string, name: string, options?: unknown) {
    this.send({ type: 'openPopup', url, name, options });
  }
  public resize(height: number) {
    this.send({ type: 'resize', height });
  }
  public scroll() {
    this.send({ type: 'scroll' });
  }
  public charge = () => {
    this.send({ type: 'charge' });
  };

  public goCharge(to: 'heart' | 'item' = 'heart') {
    // 새창(new) 타입
    if (this.serviceType === 'popup') {
      if (this.aspInfo?.type !== 'A' || !this.aspInfo?.link?.charge) return;
      switch (this.aspInfo?.link.chargeCallType) {
        case 'popup':
          window.open(this.aspInfo?.link.charge, 'aspChargePopup', 'width=800,height=800');
          break;
        case 'self':
          location.href = this.aspInfo?.link.charge;
          break;
        default:
          window.open(this.aspInfo?.link.charge, '_blank');
          break;
      }
      return;
    }

    // iframe A 타입
    if (this.aspInfo?.type === 'A') {
      this.charge();
      return;
    }

    // iframe B type
    const path = this.coreConfig.environment.includes('m-')
      ? `/asp/store/${to || 'heart'}`
      : `/asp/charge#${to || 'heart'}`;
    this.emit('navigate', path);
  }

  public goLogin() {
    if (this.aspInfo?.type !== 'A' && this.aspInfo?.type !== 'B') return;
    const aspLink = this.aspInfo?.link;
    const aspUrl = aspLink?.login;
    if (!aspUrl) return;

    if (aspLink.loginCallType === 'self') {
      location.href = aspUrl;
      return;
    } else if (aspLink.loginCallType === 'popup') {
      window.open(aspUrl, 'aspLoginPopup', 'width=800,height=800,resizable=yes,scrollbars=yes');
      return;
    } else if (aspLink.loginCallType === 'tab') {
      window.open(aspUrl, '_blank');
      return;
    }
  }

  /**
   *
   * @param loggedIn
   * @description Redirects to the ASP login page if the user is not logged in.
   */
  public nonAuthenticatedRedirect = (loggedIn: boolean) => {
    if (loggedIn) return;

    const loginPage = this.aspInfo?.link?.login;
    if (!loginPage) return;
    window.location.href = loginPage;
  };
}
